import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  UploadSimple, FilePdf, CheckCircle, WarningCircle, CircleNotch, Ruler, Check,
} from '@phosphor-icons/react';
import { apiLaminas } from '@/api/laminas';
import type { LaminaIngestada, Subida } from '@/api/laminas';

// La subida de láminas, replicando el flujo de Fieldwire: se sueltan VARIOS
// PDF a la vez, cada uno puede traer MUCHAS páginas, y cada página se archiva
// como una lámina con su propia pirámide de tiles.
//
// Dos decisiones que no son de adorno:
//
// 1. El servidor contesta 202 al instante y el avance se consulta aparte.
//    Teselar 14 láminas son varios minutos; dejar la request abierta ese rato
//    es exactamente lo que hace que una subida se sienta rota (y lo que un
//    proxy corta por timeout).
//
// 2. El nombre lo propone el cajetín del plano, pero llega SIN CONFIRMAR. Un
//    nombre inventado por un parseo que nadie revisó es peor que no tener
//    nombre: se archiva mal y nadie lo vuelve a encontrar. Por eso el panel
//    termina en una revisión, no en un "listo".

interface Props {
  onCerrar: () => void;
}

/** Una subida en vuelo, con lo que hace falta para pintar su fila. */
type EnVuelo = Subida & { local: string };

export function PanelSubida({ onCerrar }: Props) {
  const [encima, setEncima] = useState(false);
  const [subidas, setSubidas] = useState<EnVuelo[]>([]);
  const [fallo, setFallo] = useState<string | null>(null);
  const archivoRef = useRef<HTMLInputElement | null>(null);
  const qc = useQueryClient();

  const enCurso = subidas.some(s => s.estado === 'procesando');

  // Un solo intervalo para todas las subidas en curso. Se apaga cuando no
  // queda ninguna: un setInterval que sigue latiendo con la pantalla quieta es
  // una fuga silenciosa.
  useEffect(() => {
    if (!enCurso) return;
    const t = setInterval(async () => {
      const vivos = subidas.filter(s => s.estado === 'procesando');
      const frescos = await Promise.all(
        vivos.map(s => apiLaminas.subida(s.id).catch(() => null)),
      );
      setSubidas(prev => prev.map(p => {
        const f = frescos.find(x => x?.id === p.id);
        return f ? { ...f, local: p.local } : p;
      }));
      // Cada lámina que termina tiene que aparecer en la grilla de atrás sin
      // esperar a que termine el resto del archivo.
      if (frescos.some(f => f && f.estado !== 'procesando')) {
        qc.invalidateQueries({ queryKey: ['laminas'] });
      }
    }, 2000);
    return () => clearInterval(t);
  }, [enCurso, subidas, qc]);

  const recibir = async (archivos: FileList | null) => {
    setFallo(null);
    const pdfs = [...(archivos || [])].filter(f =>
      f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'));
    if (!pdfs.length) {
      setFallo('Solo se pueden subir PDF.');
      return;
    }
    for (const f of pdfs) {
      try {
        const s = await apiLaminas.subir(f);
        setSubidas(prev => [...prev, { ...s, local: f.name }]);
      } catch (e) {
        setFallo(`${f.name}: ${(e as Error).message}`);
      }
    }
  };

  const listas = subidas.flatMap(s => s.laminas);

  return (
    <div className="mb-6 rounded-lg border border-brand-gray-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-brand-gray-500">Subir láminas</h2>
        <button type="button" onClick={onCerrar}
          className="rounded px-2 py-1 text-xs text-brand-gray-400 hover:bg-brand-gray-100">
          Cerrar
        </button>
      </div>

      <div
        onDragOver={e => { e.preventDefault(); setEncima(true); }}
        onDragLeave={() => setEncima(false)}
        onDrop={e => { e.preventDefault(); setEncima(false); recibir(e.dataTransfer.files); }}
        onClick={() => archivoRef.current?.click()}
        className={`flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed px-4 py-8 transition-colors ${
          encima ? 'border-brand-green-100 bg-brand-green-100/10' : 'border-brand-gray-300 bg-brand-bg'
        }`}
      >
        <UploadSimple size={26} weight="bold" className="text-brand-gray-400" />
        <p className="mt-2 text-sm font-medium text-brand-gray-500">
          Soltá los PDF acá o hacé clic para elegirlos
        </p>
        <p className="mt-1 text-xs text-brand-gray-400">
          Varios a la vez. Un PDF de 14 páginas se archiva como 14 láminas.
        </p>
        <input
          ref={archivoRef}
          type="file"
          accept="application/pdf"
          multiple
          className="hidden"
          onChange={e => { recibir(e.target.files); e.target.value = ''; }}
        />
      </div>

      {fallo && (
        <p className="mt-2 rounded border border-brand-red-100 bg-brand-red-100/10 px-3 py-1.5 text-xs text-brand-red-200">
          {fallo}
        </p>
      )}

      {!!subidas.length && (
        <ul className="mt-3 space-y-2">
          {subidas.map(s => <FilaSubida key={s.id} s={s} />)}
        </ul>
      )}

      {!!listas.length && <Revision laminas={listas} />}
    </div>
  );
}

function FilaSubida({ s }: { s: EnVuelo }) {
  const total = s.paginasTotal;
  // Hasta que el servidor abre el PDF no se sabe cuántas páginas son: mejor
  // una barra indeterminada que un 0% que parece trabado.
  const pct = total ? Math.round((s.paginasListas / total) * 100) : null;

  return (
    <li className="rounded border border-brand-gray-200 px-3 py-2">
      <div className="flex items-center gap-2">
        <FilePdf size={16} weight="bold" className="shrink-0 text-brand-gray-400" />
        <span className="flex-1 truncate text-sm text-brand-gray-500">{s.local}</span>
        {s.estado === 'procesando' && (
          <span className="flex items-center gap-1 font-mono text-[11px] text-brand-gray-400">
            <CircleNotch size={12} weight="bold" className="animate-spin" />
            {total ? `${s.paginasListas}/${total}` : 'leyendo…'}
          </span>
        )}
        {s.estado === 'lista' && (
          <span className="flex items-center gap-1 text-[11px] font-semibold text-[#1D9E75]">
            <CheckCircle size={13} weight="bold" />
            {s.laminas.length} {s.laminas.length === 1 ? 'lámina' : 'láminas'}
          </span>
        )}
        {s.estado === 'error' && (
          <span className="flex items-center gap-1 text-[11px] font-semibold text-brand-red-200">
            <WarningCircle size={13} weight="bold" /> falló
          </span>
        )}
      </div>

      <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-brand-gray-100">
        <div
          className={`h-full transition-all ${
            s.estado === 'error' ? 'bg-brand-red-200' : 'bg-brand-green-100'
          } ${pct === null && s.estado === 'procesando' ? 'animate-pulse w-1/3' : ''}`}
          style={pct !== null ? { width: `${s.estado === 'lista' ? 100 : pct}%` } : undefined}
        />
      </div>

      {s.error && (
        <p className="mt-1.5 font-mono text-[11px] text-brand-red-200">{s.error}</p>
      )}
    </li>
  );
}

/** El último paso: revisar los nombres que propuso el cajetín. */
function Revision({ laminas }: { laminas: LaminaIngestada[] }) {
  const sinCalibrar = laminas.filter(l => !l.calibrada).length;
  const sinRotulo = laminas.filter(l => !l.rotuloLeido).length;

  return (
    <div className="mt-4 border-t border-brand-gray-100 pt-3">
      <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-brand-gray-400">
        Revisar nombres · {laminas.length}
      </h3>

      {(sinCalibrar > 0 || sinRotulo > 0) && (
        <p className="mb-2 rounded border border-brand-yellow/40 bg-brand-yellow/10 px-3 py-1.5 text-xs text-brand-gray-500">
          {sinRotulo > 0 && <>{sinRotulo} sin cajetín legible (hay que nombrarlas a mano). </>}
          {sinCalibrar > 0 && <>{sinCalibrar} sin escala: se pueden ver y anotar, pero no medir.</>}
        </p>
      )}

      <ul className="space-y-1.5">
        {laminas.map(l => <FilaRevision key={l.hojaId} l={l} />)}
      </ul>
    </div>
  );
}

function FilaRevision({ l }: { l: LaminaIngestada }) {
  const [nombre, setNombre] = useState(l.nombre);
  const [confirmado, setConfirmado] = useState(false);
  const qc = useQueryClient();

  const confirmar = async () => {
    await apiLaminas.renombrar(l.planoId, { nombre: nombre.trim() || l.nombre });
    setConfirmado(true);
    qc.invalidateQueries({ queryKey: ['laminas'] });
  };

  return (
    <li className="flex items-center gap-2">
      {l.carpeta && (
        <span className="rounded bg-brand-gray-100 px-1.5 py-0.5 font-mono text-[10px] font-bold text-brand-gray-400">
          {l.carpeta}
        </span>
      )}
      <input
        value={nombre}
        onChange={e => { setNombre(e.target.value); setConfirmado(false); }}
        className="min-w-0 flex-1 rounded border border-brand-gray-200 px-2 py-1 text-sm text-brand-gray-500 focus:outline-none focus:ring-1 focus:ring-brand-green-100"
      />
      {l.esVersionNueva && (
        <span title="Ya existía una lámina con este código: entró como versión nueva"
          className="rounded bg-[#378ADD]/15 px-1.5 py-0.5 text-[10px] font-bold text-[#0C447C]">
          v. nueva
        </span>
      )}
      {!l.calibrada && (
        <span title="Sin escala: no se puede medir sobre esta lámina">
          <Ruler size={14} weight="bold" className="text-brand-yellow" />
        </span>
      )}
      <button
        type="button"
        onClick={confirmar}
        disabled={confirmado}
        className={`flex shrink-0 items-center gap-1 rounded px-2 py-1 text-xs font-semibold transition-colors ${
          confirmado
            ? 'text-[#1D9E75]'
            : 'bg-brand-green-100 text-black hover:bg-brand-green-200'
        }`}
      >
        <Check size={12} weight="bold" /> {confirmado ? 'Confirmado' : 'Confirmar'}
      </button>
    </li>
  );
}
